import React, { useState, type FC } from 'react';
import { Space, Text, Modal } from '@g2u/ui-kit';
import { observer } from 'mobx-react-lite';
import { Dropdown, Tooltip } from 'antd';

import { CallbackSource, type JsonDataItem, type WithOptionalDataTest } from 'types';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { LinkCallbackModal } from '.';
import styles from '../callbacks.module.scss';

export namespace CallbackActions {
  export type Props = {
    isLoading?: boolean;
    lockedBy?: string;
    onCall: () => void;
    onStartBooking: () => void;
    onClose: () => void;
    onCloseWithMessage: () => void;
    onCloseWithCreate: () => void;
    onLink: (payload: JsonDataItem) => void;
    isLinked: boolean;
    isPrepaidHoursPackageProduct: boolean;
    callbackId: number;
    callbackType: CallbackSource;
  } & WithOptionalDataTest;

  export const $: FC<Props> = observer((props) => {
    const {
      onStartBooking,
      onClose,
      onCloseWithMessage,
      onCloseWithCreate,
      onCall,
      onLink,
      isLoading,
      lockedBy,
      isLinked,
      isPrepaidHoursPackageProduct,
      callbackType,
      callbackId,
    } = props;
    const [showLinkConfirmation, setShowLinkConfirmation] = useState<boolean>(false);
    const [showLinkModal, setShowLinkModal] = useState<boolean>(false);
    const isOwSale = callbackType === CallbackSource.OWSale;
    const isLinkingRequired = isOwSale && !isLinked;

    return (
      <>
        <Tooltip title={lockedBy ? `Locked by ${lockedBy}` : undefined} placement="topLeft">
          <div>
            <Dropdown.Button
              type="primary"
              placement="bottomLeft"
              trigger={'hover' as any}
              loading={isLoading}
              disabled={!!lockedBy || isLoading}
              overlayClassName={styles.callbacks__detailsDropdown}
              menu={{
                items: (isPrepaidHoursPackageProduct) ?
                  [
                    {
                      key: '1',
                      label: (
                        <Space.$
                          direction={Space.Direction.Vertical}
                          compact
                          onClick={() => (!isLinkingRequired ? onClose() : onCloseWithCreate())}
                        >
                          <Text.$ type="secondary"></Text.$>
                          <Text.$>
                            {!isLinkingRequired
                              ? 'Add a note and close the OW prepaid hours callback'
                              : 'Create customer file and close the OW prepaid hours callback'
                            }
                          </Text.$>
                        </Space.$>
                      ),
                    },
                  ]
                  : [
                  {
                    key: '1',
                    label: (
                      <Space.$ direction={Space.Direction.Vertical} compact onClick={onStartBooking}>
                        <Text.$ type="secondary">If customer needs to book a job</Text.$>
                        <Text.$>Start a new booking</Text.$>
                      </Space.$>
                    ),
                  },
                  ...(isOwSale
                    ? [
                        {
                          key: '2',
                          label: (
                            <Space.$
                              direction={Space.Direction.Vertical}
                              compact
                              onClick={() => (!isLinkingRequired ? onCloseWithMessage() : onCloseWithCreate())}
                            >
                              <Text.$ type="secondary">If customer doesn’t answer or can’t book now</Text.$>
                              <Text.$>
                                {!isLinkingRequired
                                  ? 'Leave message and close the callback'
                                  : 'Create customer file, leave message and close callback'}
                              </Text.$>
                            </Space.$>
                          ),
                        },
                        {
                          key: '3',
                          label: (
                            <Space.$ direction={Space.Direction.Vertical} compact onClick={onClose}>
                              <Text.$ type="secondary">If we can’t complete booking, close the callback</Text.$>
                              <Text.$>Add a note and close the callback</Text.$>
                            </Space.$>
                          ),
                        },
                      ]
                    : [
                        {
                          key: '2',
                          label: (
                            <Space.$
                              direction={Space.Direction.Vertical}
                              compact
                              onClick={() => (!isLinkingRequired ? onClose() : setShowLinkConfirmation(true))}
                            >
                              <Text.$ type="secondary">If customer doesn’t require a new booking</Text.$>
                              <Text.$>Close the callback</Text.$>
                            </Space.$>
                          ),
                        },
                        {
                          key: '3',
                          label: (
                            <Space.$
                              direction={Space.Direction.Vertical}
                              compact
                              onClick={() =>
                                !isLinkingRequired ? onCloseWithMessage() : setShowLinkConfirmation(true)
                              }
                            >
                              <Text.$ type="secondary">If customer doesn’t answer</Text.$>
                              <Text.$>Leave message and close the callback</Text.$>
                            </Space.$>
                          ),
                        },
                      ]),
                ],
              }}
              onClick={onCall}
            >
              Call Customer
            </Dropdown.Button>
          </div>
        </Tooltip>

        <Modal.$
          open={showLinkConfirmation}
          destroyOnClose
          maskClosable={false}
          closable={false}
          style={{ maxWidth: 292 }}
          title={
            <Space.$ size={Space.Size.Middle}>
              <ExclamationCircleFilled className={styles.callbacks__modalWarningTitleIcon} /> Link to Customer
            </Space.$>
          }
          dataTestId={'link-user-confirm'}
          onOk={() => {
            setShowLinkConfirmation(false);
            setShowLinkModal(true);
          }}
          onCancel={() => setShowLinkConfirmation(false)}
        >
          <Space.$ size={Space.Size.Middle} direction={Space.Direction.Vertical} style={{ marginBottom: -16 }}>
            <div>
              This OW callback is not yet linked to a customer record. You&apos;ll need to link it to an existing record
              or create a new one.
            </div>
            <div>Once linked, the discount will be automatically added to the customer&apos;s record.</div>
          </Space.$>
        </Modal.$>
        <LinkCallbackModal.$
          isOpen={showLinkModal}
          callbackId={callbackId}
          onLink={onLink}
          onFinish={() => setShowLinkModal(false)}
        />
      </>
    );
  });
}
